import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Flame, Send, CalendarClock, TrendingUp, Trophy, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import { Skeleton } from "@/components/ui/skeleton";

const PALETTE = ["#0A1128", "#C5A880", "#475467", "#94A3B8", "#152243"];

const KpiCard = ({ label, value, icon: Icon, sub, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    data-testid={testid}
    className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover-lift"
  >
    <div className="flex items-start justify-between">
      <div className="label-eyebrow">{label}</div>
      <Icon className="w-4 h-4 text-[#C5A880]" strokeWidth={1.75} />
    </div>
    <div className="font-serif-display text-3xl text-[#0A1128] mt-3">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </motion.div>
);

const fmtSGD = (n) => new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD", maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/dashboard/stats");
      setStats(res.data);
    } catch (_e) {
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  };

  const seed = async () => {
    try {
      await api.post("/seed-demo");
      toast.success("Demo data seeded");
      load();
    } catch (_e) { toast.error("Seed failed"); }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-8 lg:p-10 space-y-8" data-testid="dashboard-skeleton">
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-4 w-60" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-3">
              <div className="flex justify-between items-center">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </div>
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-64 w-full" />
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }
  if (!stats) return null;

  const segData = Object.entries(stats.segments || {}).map(([k, v]) => ({ name: k, value: v }));
  const stageData = Object.entries(stats.stages || {}).map(([k, v]) => ({ stage: k, count: v }));

  return (
    <div className="p-8 lg:p-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Overview</div>
          <h1 className="font-serif-display text-4xl text-[#0A1128] mt-2 tracking-tight">The desk, at a glance.</h1>
          <p className="text-slate-500 mt-2 text-sm">Swiss capital → Singapore prime residential.</p>
        </div>
        <Button
          data-testid="seed-demo-btn"
          onClick={seed}
          className="bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E] rounded-md"
        >
          Seed demo leads & listings
        </Button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard testid="kpi-total-leads" label="Total leads" value={stats.total_leads} icon={Users} />
        <KpiCard testid="kpi-hot-leads" label="Hot leads" value={stats.hot_leads} icon={Flame} sub="Qualified · Meeting · Proposal" />
        <KpiCard testid="kpi-emails-sent" label="Emails sent" value={stats.emails_sent} icon={Send} />
        <KpiCard testid="kpi-meetings" label="Meetings" value={stats.meetings_scheduled} icon={CalendarClock} />
        <KpiCard testid="kpi-pipeline" label="Pipeline value" value={fmtSGD(stats.pipeline_value_sgd)} icon={TrendingUp} />
        <KpiCard testid="kpi-won" label="Deals won" value={stats.won_count} icon={Trophy} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-lg p-6">
          <div className="label-eyebrow">Pipeline by stage</div>
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <BarChart data={stageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="stage" tick={{ fill: "#64748B", fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 12 }} />
                <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8 }} />
                <Bar dataKey="count" fill="#0A1128" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6">
          <div className="label-eyebrow">Segments</div>
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={segData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {segData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            {segData.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                <span className="text-slate-600 truncate">{s.name}</span>
                <span className="ml-auto text-slate-500">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#C5A880]" />
          <div className="label-eyebrow">Recent activity</div>
        </div>
        <div className="mt-4 divide-y divide-[#E2E8F0]">
          {(stats.recent_activities || []).length === 0 && (
            <div className="text-sm text-slate-500 py-6">No activity yet. Seed demo data or add your first lead.</div>
          )}
          {(stats.recent_activities || []).map((a) => (
            <div key={a.activity_id} className="py-3 flex items-start gap-4 text-sm">
              <div className="w-20 shrink-0 text-slate-400 text-xs mt-0.5">
                {new Date(a.created_at).toLocaleString("en-SG", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="flex-1">
                <span className="text-[#0A1128]">{a.content}</span>
                {a.author && <span className="text-slate-400"> — {a.author}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
